import auth from "@/helpers/auth";
import logger from "@/plugins/appsLogsCore";
import axios from "axios";

axios.defaults.baseURL = process.env.VUE_APP_API;
axios.defaults.headers.Accept = "application/json";

if (auth.logged()) {
  axios.defaults.headers.Authorization = "Bearer " + auth.getAccessToken();
}
const device_id = localStorage.getItem("device_identification");
if (device_id) {
  axios.defaults.headers["X-Device-Id"] = device_id;
}

let refreshPromise = null;

const terminalRefreshErrors = [
  "INVALID_REFRESH_TOKEN",
  "REFRESH_TOKEN_EXPIRED",
  "REFRESH_TOKEN_REVOKED",
  "REFRESH_TOKEN_REUSED",
];

function normalizeUrl(url = "") {
  return url.replace(/^\/+/, "");
}

function isAuthEndpoint(config = {}) {
  const url = normalizeUrl(config.url || "");
  return (
    url.includes("user/signin") ||
    url.includes("user/refresh") ||
    url.includes("user/logout")
  );
}

function unwrapPayload(response) {
  return response && response.data && response.data.data
    ? response.data.data
    : response && response.data
    ? response.data
    : {};
}

function requestMetadata(config = {}, extra = {}) {
  return {
    method: config.method,
    url: config.url,
    status: config.response && config.response.status,
    ...extra,
  };
}

function normalizeLegacyMessage(message) {
  if (!message || typeof message !== "string") return null;
  const normalized = message.toLowerCase();

  if (normalized.includes("token") && normalized.includes("expired")) {
    return "TOKEN_EXPIRED";
  }
  if (
    normalized.includes("unauthenticated") ||
    normalized.includes("unauthorized")
  ) {
    return "UNAUTHORIZED";
  }
  if (normalized.includes("rate") && normalized.includes("limit")) {
    return "RATE_LIMITED";
  }

  return null;
}

export function getApiErrorCode(error) {
  const res = error && error.response;
  const data = res && res.data;

  return (
    (data && data.error && data.error.code) ||
    (data && data.code) ||
    (data && data.data && data.data.error && data.data.error.code) ||
    (data && data.data && data.data.code) ||
    normalizeLegacyMessage(
      (data && (data.message || data.error || data.data)) || null
    ) ||
    (res && res.status === 429 ? "RATE_LIMITED" : null) ||
    (res && res.status === 401 ? "UNAUTHORIZED" : null)
  );
}

async function refreshAccessToken() {
  const startedAt = Date.now();
  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) {
    logger.auth("auth.refresh_token_missing", {
      reason: "refresh_requested_without_refresh_token",
    });
    throw new Error("Missing refresh token");
  }

  logger.auth("auth.refresh_started");

  const response = await axios.post("user/refresh", {
    refresh_token: refreshToken,
  });

  const payload = unwrapPayload(response);
  const accessToken = auth.saveAuthResponse(payload);

  if (!accessToken) {
    logger.auth("auth.refresh_failed", {
      reason: "missing_access_token_in_refresh_response",
    });
    throw new Error("Refresh response did not include an access token");
  }

  axios.defaults.headers.Authorization = "Bearer " + accessToken;
  logger.auth("auth.refresh_succeeded", { durationMs: Date.now() - startedAt });
  return accessToken;
}

function refreshWithLock() {
  if (refreshPromise) {
    logger.auth("auth.refresh_lock_reused");
  } else {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers.Accept = "application/json";

  const token = auth.getAccessToken();
  if (token) {
    config.headers.Authorization = "Bearer " + token;
  }

  const deviceId = localStorage.getItem("device_identification");
  if (deviceId) {
    config.headers["X-Device-Id"] = deviceId;
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const code = getApiErrorCode(error);

    if (terminalRefreshErrors.includes(code)) {
      logger.auth(
        "auth.terminal_refresh_error",
        requestMetadata(originalRequest, { code })
      );
      auth.clearAuthState("terminal_refresh_error", { code });
      error.authFailed = true;
      return Promise.reject(error);
    }

    if (code === "UNAUTHORIZED" && !auth.hasRefreshToken()) {
      logger.auth(
        "auth.unauthorized_without_refresh_token",
        requestMetadata(originalRequest, { code })
      );
      auth.clearAuthState("unauthorized_without_refresh_token", { code });
      error.authFailed = true;
      return Promise.reject(error);
    }

    const canRefresh =
      auth.hasRefreshToken() &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest) &&
      (code === "TOKEN_EXPIRED" || code === "UNAUTHORIZED");

    if (canRefresh) {
      originalRequest._retry = true;

      try {
        const token = await refreshWithLock();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = "Bearer " + token;
        return axios(originalRequest);
      } catch (refreshError) {
        const refreshCode = getApiErrorCode(refreshError);
        logger.auth(
          "auth.refresh_failed",
          requestMetadata(originalRequest, {
            reason: "refresh_request_failed",
            code: refreshCode,
            status: refreshError.response && refreshError.response.status,
          })
        );
        if (
          terminalRefreshErrors.includes(refreshCode) ||
          refreshCode === "UNAUTHORIZED"
        ) {
          auth.clearAuthState("refresh_failed", { code: refreshCode });
          refreshError.authFailed = true;
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// axios.defaults.headers.post["Content-Type"] = "application/json";
// axios.defaults.headers.post["Accept"] = "application/json";

export default axios;
